import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  IconButton,
  TextField,
  List, 
  ListItem, 
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Drawer,
  InputAdornment,
  Button,
  useTheme,
  styled,
  useMediaQuery,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemButton,
  Link,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  Layers,
  ExpandLess,
  Search,
  Home,
  Nature,
  Menu as MenuIcon,
  School,
  Close,
  FamilyRestroom,
  Man,
  TrendingUp,
  Public,
  ExpandMore,
  Groups,
  LocalHospital,
  Person,
  Grass,
  Spa,
  BugReport,
  Hive,
  Water as WaterIcon,
  Groups3,
  Handshake,
  Diversity3,
  House,
  Roofing,
  Map,
  Pets as LivestockIcon,
  EggAlt as PoultryIcon,
  Cottage as EmergencyHomeIcon, 
  AccessibilityNew, 
  ChildCare, 
  Terrain, 
  Engineering 
} from '@mui/icons-material';

// Constantes de style
const drawerWidth = 240;
const primaryColor = '#E30613';
const secondaryColor = '#008D36';
const backgroundColor = '#f5f5f5';

// Composants stylisés
const MainContainer = styled('div')({
  display: 'flex',
  backgroundColor: backgroundColor,
  minHeight: '100vh',
  flexDirection: 'column'
});

const MainContentWithDrawer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  marginTop: '64px',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    marginTop: '56px'
  }
}));

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
  backgroundColor: backgroundColor,
  height: 'calc(100vh - 64px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    height: 'calc(100vh - 56px)'
  }
}));

const DrawerWrapper = styled('div')(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  overflow: 'auto',
  height: 'calc(100vh - 64px)',
  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
  backgroundColor: '#fff',
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    zIndex: 1300,
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    '&.drawerOpen': {
      transform: 'translateX(0)',
    },
    '&.drawerClose': {
      transform: 'translateX(-100%)',
    },
  }
}));

const MobileDrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const StyledTableHead = styled(TableHead)({
  backgroundColor: primaryColor,
  '& th': {
    color: 'white',
    fontWeight: 'bold'
  }
});

const ProvinceRow = styled(TableRow)({
  backgroundColor: '#e0e0e0',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#d0d0d0 !important'
  }
});

const CommuneRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: '#f5f5f5 !important'
  }
});

const menuItems = [
  { text: 'Ménages par type', icon: <FamilyRestroom color="primary" />, to: '/RGPHAEDataPage' },
  { text: 'Population par localisation', icon: <Public color="primary" />, to: '/PopulationDataPage' },
  { text: 'Ménages collectifs', icon: <Groups color="primary" />, to: '/PopulationCollectiveHouseholdsPage' },
  { text: 'Sans-abris', icon: <EmergencyHomeIcon color="primary" />, to: '/HomelessPopulationPage' },
  { text: 'Ménages ordinaires', icon: <Home color="primary" />, to: '/PopulationResidentPage' },
  { text: 'Sexe et masculinité', icon: <Man color="primary" />, to: '/PopulationDataPageM' },
  { text: 'Rapport de masculinité', icon: <TrendingUp color="primary" />, to: '/MasculinityRatioPage' },
  { text: 'Population par âge', icon: <ChildCare color="primary" />, to: '/PopulationDataPageAge' },
  { text: "Grands groupes d'âge", icon: <AccessibilityNew color="primary" />, to: '/PopulationAgeGroupPageGrand' },
  { text: "Groupes d'âge (éducation)", icon: <School color="primary" />, to: '/PopulationByAgeGroupPage' },
  { text: 'Groupes d\'âge (santé)', icon: <LocalHospital color="primary" />, to: '/AgeGroupPopulationPage' },
  { text: 'Adultes (18+)', icon: <Person color="primary" />, to: '/AdultPopulationPage' },
  
  // Agriculture
  { text: 'Ménages agricoles', icon: <Nature color="primary" />, to: '/AgriculturalHouseholdsPage' },
  { text: 'Pratiques agricoles', icon: <Grass color="primary" />, to: '/AgriculturalHouseholdsPages' },
  { text: 'Types de cultures', icon: <Spa color="primary" />, to: '/AgriculturalHouseholdsPage20' },
  
  // Élevage
  { text: 'Élevage par espèces', icon: <LivestockIcon color="primary" />, to: '/FarmingHouseholdsPage' },
  { text: 'Élevage de volailles', icon: <PoultryIcon color="primary" />, to: '/poultryFarmingPage' },
  { text: 'Élevage spécial', icon: <BugReport color="primary" />, to: '/insectFarmingHouseholdsPage' },
  { text: 'Répartition cheptel', icon: <LivestockIcon color="primary" />, to: '/livestockDistributionPage' },
  { text: 'Répartition volailles', icon: <PoultryIcon color="primary" />, to: '/poultryDistributionPage' },
  
  // Matériel agricole
  { text: 'Équipements agricoles', icon: <Engineering color="primary" />, to: '/agriculturalEquipmentPage' },
  { text: 'Apiculture', icon: <Hive color="primary" />, to: '/beekeepingHouseholdsPage' },
  { text: 'Pêche/aquaculture', icon: <WaterIcon color="primary" />, to: '/fishingHouseholdsPage' },
  
  // Associations
  { text: 'Interventions associations', icon: <Groups3 color="primary" />, to: '/associationInterventionPage' },
  { text: 'Interventions coopératives', icon: <Handshake color="primary" />, to: '/cooperativeInterventionPage' },
  { text: 'Interventions ONG', icon: <Diversity3 color="primary" />, to: '/interventionProjectsPage' },
  
  // Habitat
  { text: "Type d'habitat", icon: <House color="primary" />, to: '/housingDistributionPage' },
  { text: 'Matériau toit', icon: <Roofing color="primary" />, to: '/roofMaterialPage' },
  { text: 'Matériau sol', icon: <Layers color="primary" />, to: '/housingMaterialPage' },
  { text: 'Ménages agricoles', icon: <Terrain color="primary" />, to: '/agriculturalHouseholdsPageProvCom' },
  { text: 'Population par âge/résidence', icon: <Map color="primary" />, to: '/populationAgeResidencePage' },
];

// Structure des données
const roofMaterialData = {
  title: "Répartition des ménages ordinaires par milieu de résidence, province et commune selon le principal matériau du toit",
  provinces: [
    {
      name: "BUHUMUZA",
      communes: [
        { 
          name: "BUTAGANZWA", 
          tôle: "14 579", 
          tuileLocale: "5 559", 
          tuileArdoise: "14", 
          eternit: "14", 
          beton: "-", 
          sheeting: "37", 
          paille: "2 469", 
          cartonPlastique: "80", 
          total: "22 752" 
        },
        { 
          name: "BUTIHINDA", 
          tôle: "70 124", 
          tuileLocale: "18 029", 
          tuileArdoise: "189", 
          eternit: "52", 
          beton: "-", 
          sheeting: "1 653", 
          paille: "7 480", 
          cartonPlastique: "2 009", 
          total: "99 536" 
        },
        { 
          name: "CANKUZO", 
          tôle: "21 565", 
          tuileLocale: "6 944", 
          tuileArdoise: "96", 
          eternit: "13", 
          beton: "-", 
          sheeting: "56", 
          paille: "4 816", 
          cartonPlastique: "208", 
          total: "33 698" 
        },
        { 
          name: "GISAGARA", 
          tôle: "42 848", 
          tuileLocale: "8 690", 
          tuileArdoise: "203", 
          eternit: "124", 
          beton: "-", 
          sheeting: "480", 
          paille: "8 459", 
          cartonPlastique: "1 106", 
          total: "61 910" 
        },
        { 
          name: "GISURU", 
          tôle: "50 177", 
          tuileLocale: "9 075", 
          tuileArdoise: "103", 
          eternit: "102", 
          beton: "-", 
          sheeting: "101", 
          paille: "10 380", 
          cartonPlastique: "228", 
          total: "70 166" 
        },
        { 
          name: "MUYINGA", 
          tôle: "74 999", 
          tuileLocale: "15 819", 
          tuileArdoise: "486", 
          eternit: "102", 
          beton: "-", 
          sheeting: "876", 
          paille: "13 755", 
          cartonPlastique: "1 558", 
          total: "107 595" 
        },
        { 
          name: "RUYIGI", 
          tôle: "34 007", 
          tuileLocale: "6 194", 
          tuileArdoise: "138", 
          eternit: "9", 
          beton: "-", 
          sheeting: "85", 
          paille: "5 102", 
          cartonPlastique: "166", 
          total: "45 701" 
        }
      ],
      tôle: "308 299",
      tuileLocale: "70 310",
      tuileArdoise: "1 229",
      eternit: "416",
      beton: "-",
      sheeting: "3 288",
      paille: "52 461",
      cartonPlastique: "5 355",
      total: "441 358"
    },
    {
      name: "BUJUMBURA",
      communes: [
        { 
          name: "BUBANZA", 
          tôle: "28 027", 
          tuileLocale: "15 940", 
          tuileArdoise: "281", 
          eternit: "27", 
          beton: "5", 
          sheeting: "349", 
          paille: "4 322", 
          cartonPlastique: "466", 
          total: "49 417" 
        },
        { 
          name: "BUKINANYANA", 
          tôle: "46 971", 
          tuileLocale: "3 916", 
          tuileArdoise: "125", 
          eternit: "77", 
          beton: "2", 
          sheeting: "244", 
          paille: "4 234", 
          cartonPlastique: "487", 
          total: "56 056" 
        },
        { 
          name: "CIBITOKE", 
          tôle: "51 404", 
          tuileLocale: "555", 
          tuileArdoise: "48", 
          eternit: "13", 
          beton: "2", 
          sheeting: "227", 
          paille: "3 582", 
          cartonPlastique: "255", 
          total: "56 086" 
        },
        { 
          name: "ISARE", 
          tôle: "24 046", 
          tuileLocale: "4 256", 
          tuileArdoise: "122", 
          eternit: "24", 
          beton: "4", 
          sheeting: "974", 
          paille: "699", 
          cartonPlastique: "478", 
          total: "30 603" 
        },
        { 
          name: "MPANDA", 
          tôle: "67 476", 
          tuileLocale: "2 344", 
          tuileArdoise: "111", 
          eternit: "130", 
          beton: "12", 
          sheeting: "627", 
          paille: "4 147", 
          cartonPlastique: "1 080", 
          total: "75 927" 
        },
        { 
          name: "MUGERE", 
          tôle: "101 895", 
          tuileLocale: "2 872", 
          tuileArdoise: "935", 
          eternit: "494", 
          beton: "554", 
          sheeting: "373", 
          paille: "1 598", 
          cartonPlastique: "1 364", 
          total: "110 085" 
        },
        { 
          name: "MUGINA", 
          tôle: "41 127", 
          tuileLocale: "961", 
          tuileArdoise: "67", 
          eternit: "39", 
          beton: "1", 
          sheeting: "377", 
          paille: "3 604", 
          cartonPlastique: "352", 
          total: "46 528" 
        },
        { 
          name: "MUHUTA", 
          tôle: "18 322", 
          tuileLocale: "108", 
          tuileArdoise: "105", 
          eternit: "2", 
          beton: "-", 
          sheeting: "50", 
          paille: "3 327", 
          cartonPlastique: "1 250", 
          total: "23 164" 
        },
        { 
          name: "MUKAZA", 
          tôle: "39 094", 
          tuileLocale: "761", 
          tuileArdoise: "355", 
          eternit: "713", 
          beton: "394", 
          sheeting: "78", 
          paille: "83", 
          cartonPlastique: "62", 
          total: "41 540" 
        },
        { 
          name: "NTAHANGWA", 
          tôle: "205 597", 
          tuileLocale: "4 462", 
          tuileArdoise: "690", 
          eternit: "940", 
          beton: "798", 
          sheeting: "1 561", 
          paille: "1 116", 
          cartonPlastique: "955", 
          total: "216 119" 
        },
        { 
          name: "RWIBAGA", 
          tôle: "19 308", 
          tuileLocale: "1 055", 
          tuileArdoise: "35", 
          eternit: "16", 
          beton: "-", 
          sheeting: "158", 
          paille: "2 742", 
          cartonPlastique: "1 212", 
          total: "24 526" 
        }
      ],
      tôle: "643 267",
      tuileLocale: "37 230",
      tuileArdoise: "2 874",
      eternit: "2 475",
      beton: "1 772",
      sheeting: "5 018",
      paille: "29 454",
      cartonPlastique: "7 961",
      total: "730 051"
    },
    {
      name: "BURUNGA",
      communes: [
        { 
          name: "BURURI", 
          tôle: "34 967", 
          tuileLocale: "2 173", 
          tuileArdoise: "252", 
          eternit: "48", 
          beton: "-", 
          sheeting: "29", 
          paille: "3 822", 
          cartonPlastique: "138", 
          total: "41 429" 
        },
        { 
          name: "MAKAMBA", 
          tôle: "66 839", 
          tuileLocale: "3 844", 
          tuileArdoise: "243", 
          eternit: "107", 
          beton: "1", 
          sheeting: "168", 
          paille: "6 140", 
          cartonPlastique: "343", 
          total: "77 685" 
        },
        { 
          name: "MATANA", 
          tôle: "32 053", 
          tuileLocale: "5 103", 
          tuileArdoise: "76", 
          eternit: "22", 
          beton: "-", 
          sheeting: "77", 
          paille: "5 976", 
          cartonPlastique: "349", 
          total: "43 656" 
        },
        { 
          name: "MUSONGATI", 
          tôle: "52 479", 
          tuileLocale: "7 467", 
          tuileArdoise: "90", 
          eternit: "21", 
          beton: "4", 
          sheeting: "310", 
          paille: "10 105", 
          cartonPlastique: "607", 
          total: "71 083" 
        },
        { 
          name: "NYANZA", 
          tôle: "70 974", 
          tuileLocale: "875", 
          tuileArdoise: "44", 
          eternit: "41", 
          beton: "2", 
          sheeting: "343", 
          paille: "3 142", 
          cartonPlastique: "1 141", 
          total: "76 562" 
        },
        { 
          name: "RUMONGE", 
          tôle: "80 577", 
          tuileLocale: "1 263", 
          tuileArdoise: "139", 
          eternit: "18", 
          beton: "8", 
          sheeting: "403", 
          paille: "3 608", 
          cartonPlastique: "1 524", 
          total: "87 540" 
        },
        { 
          name: "RUTANA", 
          tôle: "40 815", 
          tuileLocale: "1 588", 
          tuileArdoise: "67", 
          eternit: "153", 
          beton: "2", 
          sheeting: "100", 
          paille: "4 236", 
          cartonPlastique: "296", 
          total: "47 257" 
        }
      ],
      tôle: "378 704",
      tuileLocale: "22 313",
      tuileArdoise: "911",
      eternit: "410",
      beton: "17",
      sheeting: "1 430",
      paille: "37 029",
      cartonPlastique: "4 398",
      total: "445 212"
    },
    {
      name: "BUTANYERERA",
      communes: [
        { 
          name: "BUSONI", 
          tôle: "60 537", 
          tuileLocale: "12 128", 
          tuileArdoise: "378", 
          eternit: "25", 
          beton: "1", 
          sheeting: "1 016", 
          paille: "5 893", 
          cartonPlastique: "1 271", 
          total: "81 249" 
        },
        { 
          name: "KAYANZA", 
          tôle: "8 612", 
          tuileLocale: "62 314", 
          tuileArdoise: "240", 
          eternit: "164", 
          beton: "4", 
          sheeting: "198", 
          paille: "2 188", 
          cartonPlastique: "573", 
          total: "74 293" 
        },
        { 
          name: "KIREMBA", 
          tôle: "21 187", 
          tuileLocale: "37 558", 
          tuileArdoise: "608", 
          eternit: "60", 
          beton: "-", 
          sheeting: "213", 
          paille: "8 865", 
          cartonPlastique: "326", 
          total: "68 817" 
        },
        { 
          name: "KIRUNDO", 
          tôle: "65 287", 
          tuileLocale: "32 849", 
          tuileArdoise: "304", 
          eternit: "78", 
          beton: "1", 
          sheeting: "881", 
          paille: "9 847", 
          cartonPlastique: "694", 
          total: "109 941" 
        },
        { 
          name: "MATONGO", 
          tôle: "2 965", 
          tuileLocale: "56 572", 
          tuileArdoise: "186", 
          eternit: "8", 
          beton: "2", 
          sheeting: "103", 
          paille: "1 111", 
          cartonPlastique: "255", 
          total: "61 202" 
        },
        { 
          name: "MUHANGA", 
          tôle: "1 677", 
          tuileLocale: "51 574", 
          tuileArdoise: "381", 
          eternit: "4", 
          beton: "-", 
          sheeting: "57", 
          paille: "1 361", 
          cartonPlastique: "145", 
          total: "55 199" 
        },
        { 
          name: "NGOZI", 
          tôle: "36 025", 
          tuileLocale: "35 363", 
          tuileArdoise: "766", 
          eternit: "413", 
          beton: "6", 
          sheeting: "506", 
          paille: "2 831", 
          cartonPlastique: "832", 
          total: "76 742" 
        },
        { 
          name: "TANGARA", 
          tôle: "14 780", 
          tuileLocale: "37 557", 
          tuileArdoise: "715", 
          eternit: "237", 
          beton: "-", 
          sheeting: "302", 
          paille: "6 825", 
          cartonPlastique: "338", 
          total: "60 754" 
        }
      ],
      tôle: "211 070",
      tuileLocale: "325 915",
      tuileArdoise: "3 578",
      eternit: "989",
      beton: "14",
      sheeting: "3 276",
      paille: "38 921",
      cartonPlastique: "4 434",
      total: "588 197"
    },
    {
      name: "GITEGA",
      communes: [
        { 
          name: "BUGENDANA", 
          tôle: "10 613", 
          tuileLocale: "41 394", 
          tuileArdoise: "240", 
          eternit: "56", 
          beton: "-", 
          sheeting: "115", 
          paille: "1 438", 
          cartonPlastique: "157", 
          total: "54 013" 
        },
        { 
          name: "GISHUBI", 
          tôle: "19 797", 
          tuileLocale: "35 214", 
          tuileArdoise: "528", 
          eternit: "178", 
          beton: "-", 
          sheeting: "16", 
          paille: "3 244", 
          cartonPlastique: "54", 
          total: "59 031" 
        },
        { 
          name: "GITEGA", 
          tôle: "62 744", 
          tuileLocale: "54 342", 
          tuileArdoise: "850", 
          eternit: "274", 
          beton: "60", 
          sheeting: "97", 
          paille: "1 790", 
          cartonPlastique: "196", 
          total: "120 353" 
        },
        { 
          name: "KARUSI", 
          tôle: "37 312", 
          tuileLocale: "30 634", 
          tuileArdoise: "504", 
          eternit: "230", 
          beton: "-", 
          sheeting: "238", 
          paille: "13 182", 
          cartonPlastique: "300", 
          total: "82 400" 
        },
        { 
          name: "KIGANDA", 
          tôle: "5 890", 
          tuileLocale: "26 857", 
          tuileArdoise: "485", 
          eternit: "92", 
          beton: "2", 
          sheeting: "23", 
          paille: "657", 
          cartonPlastique: "81", 
          total: "34 087" 
        },
        { 
          name: "MURAMVYA", 
          tôle: "11 376", 
          tuileLocale: "25 240", 
          tuileArdoise: "719", 
          eternit: "21", 
          beton: "-", 
          sheeting: "65", 
          paille: "622", 
          cartonPlastique: "111", 
          total: "38 154" 
        },
        { 
          name: "MWARO", 
          tôle: "16 710", 
          tuileLocale: "14 356", 
          tuileArdoise: "250", 
          eternit: "30", 
          beton: "-", 
          sheeting: "16", 
          paille: "3 370", 
          cartonPlastique: "69", 
          total: "34 801" 
        },
        { 
          name: "NYABIHANGA", 
          tôle: "11 815", 
          tuileLocale: "27 796", 
          tuileArdoise: "78", 
          eternit: "25", 
          beton: "1", 
          sheeting: "23", 
          paille: "1 842", 
          cartonPlastique: "70", 
          total: "41 650" 
        },
        { 
          name: "SHOMBO", 
          tôle: "12 423", 
          tuileLocale: "38 838", 
          tuileArdoise: "703", 
          eternit: "14", 
          beton: "-", 
          sheeting: "100", 
          paille: "2 968", 
          cartonPlastique: "242", 
          total: "55 288" 
        }
      ],
      tôle: "188 680",
      tuileLocale: "294 671",
      tuileArdoise: "4 357",
      eternit: "920",
      beton: "63",
      sheeting: "693",
      paille: "29 113",
      cartonPlastique: "1 280",
      total: "519 777"
    },
    {
      name: "BURUNDI",
      communes: [],
      tôle: "1 730 020",
      tuileLocale: "750 439",
      tuileArdoise: "12 949",
      eternit: "5 210",
      beton: "1 866",
      sheeting: "13 705",
      paille: "186 978",
      cartonPlastique: "23 428",
      total: "2 724 595"
    }
  ],
  urban: {
    tôle: "625 003",
    tuileLocale: "43 857",	
    tuileArdoise: "3 270",
    eternit: "2 910",
    beton: "1 817",
    sheeting: "2 969",
    paille: "6 160",
    cartonPlastique: "2 517",
    total: "688 503"
  },
  rural: {
    tôle: "1 105 017",
    tuileLocale: "706 582",
    tuileArdoise: "9 679",
    eternit: "2 300",
    beton: "49",
    sheeting: "10 736",
    paille: "180 818",
    cartonPlastique: "20 911",
    total: "2 036 092"
  }
};

const RoofMaterialPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedProvinces, setExpandedProvinces] = useState({});
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [openKeyStats, setOpenKeyStats] = useState(true);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleProvince = (provinceName) => {
    setExpandedProvinces(prev => ({
      ...prev,
      [provinceName]: !prev[provinceName]
    }));
  };
  const filteredProvinces = roofMaterialData.provinces.map(province => {
    if (!searchTerm) return province;
    
    const filteredCommunes = province.communes.filter(commune =>
      commune.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return {
      ...province,
      communes: filteredCommunes,
      show: filteredCommunes.length > 0 || 
            province.name.toLowerCase().includes(searchTerm.toLowerCase())
    };
  }).filter(province => province.show || !searchTerm);

// Calcul des indicateurs clés
// Calcul des indicateurs clés - Version corrigée
const keyStats = {
  totalHouseholds: roofMaterialData.provinces[5].total,

  urbanPercentage: (
    (parseFloat(roofMaterialData.urban.total.replace(/\s/g, '')) / 
     parseFloat(roofMaterialData.provinces[5].total.replace(/\s/g, ''))) * 100
  ).toFixed(1),

  mostCommonMaterial: {
    name: "Tôle",
    value: roofMaterialData.provinces[5].tôle,
    percentage: (
      (parseFloat(roofMaterialData.provinces[5].tôle.replace(/\s/g, '')) / 
       parseFloat(roofMaterialData.provinces[5].total.replace(/\s/g, ''))) * 100
    ).toFixed(1)
  },

  ruralTraditionalMaterials: {
    paille: roofMaterialData.rural.paille,
    percentage: (
      (parseFloat(roofMaterialData.rural.paille.replace(/\s/g, '')) / 
       parseFloat(roofMaterialData.rural.total.replace(/\s/g, ''))) * 100
    ).toFixed(1)
  }
};
  // Composant du menu latéral
  const drawer = (
    <div>
      {isMobile && (
        <MobileDrawerHeader>
          <IconButton onClick={handleDrawerToggle}>
            <Close color="primary" />
          </IconButton>
        </MobileDrawerHeader>
      )}
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.to}
              onClick={isMobile ? handleDrawerToggle : undefined}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <MainContainer>
      <CssBaseline />
      <NavigationBar />
      <MainContentWithDrawer>
        {!isMobile ? (
          <DrawerWrapper>
            {drawer}
          </DrawerWrapper>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
        )}

        <ContentWrapper>
          <Container maxWidth="lg">
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<MenuIcon />} 
                  onClick={handleDrawerToggle}
                  sx={{ color: primaryColor, borderColor: primaryColor }}
                >
                  Menu
                </Button>
              </Box>
            )}
        
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: { xs: 1, sm: 3 }, mb: 3, borderLeft: `4px solid ${primaryColor}` }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2 }}>
                    <Typography variant="h6" component="h2" fontWeight="bold" color="primary" sx={{ mb: { xs: 1, sm: 0 } }}>
                      {roofMaterialData.title}
                    </Typography>
                  </Box>
                  <Grid container spacing={1} style={{ marginTop: '5px',marginBottom: '5px' }}>
                    <Grid item xs={12} sm={6} md={6} ></Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        size="small"
                        placeholder="Rechercher province/commune..."
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: { xs: '100%', sm: '480px' } }}
                      />
                    </Grid>
                  </Grid>
                  
                  {/* Section des indicateurs clés */}
                  <Accordion expanded={openKeyStats} onChange={() => setOpenKeyStats(!openKeyStats)} sx={{ mb: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Indicateurs Clés sur les Matériaux de Toiture
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Ménages Totaux
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {keyStats.totalHouseholds}
                              </Typography>
                              <Typography variant="caption">
                                Ménages ordinaires
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Ménages Urbains
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {keyStats.urbanPercentage}%
                              </Typography>
                              <Typography variant="caption">
                                {roofMaterialData.urban.total} ménages
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Matériau le plus utilisé
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {keyStats.mostCommonMaterial.name}
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="baseline">
                                <Typography variant="body1">
                                  {keyStats.mostCommonMaterial.value}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ({keyStats.mostCommonMaterial.percentage}%)
                                </Typography>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary">
                                Toits en paille (rural)
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {keyStats.ruralTraditionalMaterials.paille}
                              </Typography>
                              <Typography variant="body1">
                                {keyStats.ruralTraditionalMaterials.percentage}%
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  
                  <TableContainer component={Paper} sx={{ mb: 2, overflowX: 'auto' }}>
                    <Table size="small" aria-label="Tableau des matériaux de toiture">
                      <StyledTableHead>
                        <TableRow>
                          <TableCell>Zone</TableCell>
                          <TableCell align="right">Tôle</TableCell>
                          <TableCell align="right">Tuile locale</TableCell>
                          <TableCell align="right">Tuile/Ardoise industrielle</TableCell>
                          <TableCell align="right">Eternit</TableCell>
                          <TableCell align="right">Béton</TableCell>
                          <TableCell align="right">Sheeting/bâche</TableCell>
                          <TableCell align="right">Paille</TableCell>
                          <TableCell align="right">Carton/plastique/sachet</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </StyledTableHead>
                      <TableBody>
<ProvinceRow hover>
  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
    BURUNDI
  </TableCell>
  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{roofMaterialData.provinces[5].tôle}</TableCell>
  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{roofMaterialData.provinces[5].tuileLocale}</TableCell>
  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{roofMaterialData.provinces[5].tuileArdoise}</TableCell>
  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{roofMaterialData.provinces[5].eternit}</TableCell>
  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{roofMaterialData.provinces[5].beton}</TableCell>
  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{roofMaterialData.provinces[5].sheeting}</TableCell>
  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{roofMaterialData.provinces[5].paille}</TableCell>
  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{roofMaterialData.provinces[5].cartonPlastique}</TableCell>
  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{roofMaterialData.provinces[5].total}</TableCell>
</ProvinceRow>

                        {/* Lignes pour milieu urbain et rural */}
                        <ProvinceRow hover>
                          <TableCell component="th" scope="row">
                            Urbain
                          </TableCell>
                          <TableCell align="right">{roofMaterialData.urban.tôle}</TableCell>
                          <TableCell align="right">{roofMaterialData.urban.tuileLocale}</TableCell>
                          <TableCell align="right">{roofMaterialData.urban.tuileArdoise}</TableCell>
                          <TableCell align="right">{roofMaterialData.urban.eternit}</TableCell>
                          <TableCell align="right">{roofMaterialData.urban.beton}</TableCell>
                          <TableCell align="right">{roofMaterialData.urban.sheeting}</TableCell>
                          <TableCell align="right">{roofMaterialData.urban.paille}</TableCell>
                          <TableCell align="right">{roofMaterialData.urban.cartonPlastique}</TableCell>
                          <TableCell align="right">{roofMaterialData.urban.total}</TableCell>
                        </ProvinceRow>

                        <ProvinceRow hover>
                          <TableCell component="th" scope="row">
                            Rural
                          </TableCell>
                          <TableCell align="right">{roofMaterialData.rural.tôle}</TableCell>
                          <TableCell align="right">{roofMaterialData.rural.tuileLocale}</TableCell>
                          <TableCell align="right">{roofMaterialData.rural.tuileArdoise}</TableCell>
                          <TableCell align="right">{roofMaterialData.rural.eternit}</TableCell>
                          <TableCell align="right">{roofMaterialData.rural.beton}</TableCell>
                          <TableCell align="right">{roofMaterialData.rural.sheeting}</TableCell>
                          <TableCell align="right">{roofMaterialData.rural.paille}</TableCell>
                          <TableCell align="right">{roofMaterialData.rural.cartonPlastique}</TableCell>
                          <TableCell align="right">{roofMaterialData.rural.total}</TableCell>
                        </ProvinceRow>

                        {/* Données par province et commune */}
                        {filteredProvinces.slice(0, 5).map((province, index) => (
                          <React.Fragment key={index}>
                            <ProvinceRow 
                              hover 
                              onClick={() => toggleProvince(province.name)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell component="th" scope="row">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {expandedProvinces[province.name] ? <ExpandLess /> : <ExpandMore />}
                                  {province.name}
                                </Box>
                              </TableCell>
                              <TableCell align="right">{province.tôle}</TableCell>
                              <TableCell align="right">{province.tuileLocale}</TableCell>
                              <TableCell align="right">{province.tuileArdoise}</TableCell>
                              <TableCell align="right">{province.eternit}</TableCell>
                              <TableCell align="right">{province.beton}</TableCell>
                              <TableCell align="right">{province.sheeting}</TableCell>
                              <TableCell align="right">{province.paille}</TableCell>
                              <TableCell align="right">{province.cartonPlastique}</TableCell>
                              <TableCell align="right">{province.total}</TableCell>
                            </ProvinceRow>
                            
                            <TableRow>
                              <TableCell style={{ padding: 0 }} colSpan={10}>
                                <Collapse in={expandedProvinces[province.name] || !!searchTerm} timeout="auto" unmountOnExit>
                                  <Table size="small" sx={{ backgroundColor: '#fafafa' }}>
                                    <TableBody>
                                      {province.communes.map((commune, idx) => (
                                        <CommuneRow key={idx} hover>
                                          <TableCell sx={{ pl: 6 }}>{commune.name}</TableCell>
                                          <TableCell align="right">{commune.tôle}</TableCell>
                                          <TableCell align="right">{commune.tuileLocale}</TableCell>
                                          <TableCell align="right">{commune.tuileArdoise}</TableCell>
                                          <TableCell align="right">{commune.eternit}</TableCell>
                                          <TableCell align="right">{commune.beton}</TableCell>
                                          <TableCell align="right">{commune.sheeting}</TableCell>
                                          <TableCell align="right">{commune.paille}</TableCell>
                                          <TableCell align="right">{commune.cartonPlastique}</TableCell>
                                          <TableCell align="right">{commune.total}</TableCell>
                                        </CommuneRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
                
                {/* Section d'information */}
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" color="primary">
                      À propos des matériaux de toiture
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" paragraph>
                      Les matériaux de toiture utilisés par les ménages reflètent les conditions de vie et le niveau de développement économique. Les principaux matériaux recensés sont:
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      <ListItem>
                        <ListItemText 
                          primary="• Tôle: Matériau métallique largement utilisé pour sa durabilité" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• Tuile locale: Fabriquée artisanalement, typique des zones rurales" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• Tuile/Ardoise industrielle: Matériaux modernes de meilleure qualité" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• Paille: Matériau traditionnel encore utilisé dans certaines régions" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="• Carton/plastique/sachet: Solution précaire pour les ménages les plus pauvres" 
                        />
                      </ListItem>
                    </List>
                    <Typography variant="body2" mt={2}>
                      Ces données montrent que {keyStats.mostCommonMaterial.percentage}% des ménages utilisent la tôle comme principal matériau de toiture.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                
                
                                {/* Notes méthodologiques */}
                               <Paper sx={{ p: 2, backgroundColor: '#f0f0f0', borderLeft: `4px solid ${secondaryColor}` }}>
  <Typography variant="subtitle2" gutterBottom color="primary">
    Notes méthodologiques
  </Typography>
  <Typography variant="body2" color="text.secondary">
    Les données présentées proviennent du Recensement Général de la Population, de l'Habitat, de l'Agriculture et de l'Élevage (RGPHAE) réalisé en 2024.
    Cette analyse porte exclusivement sur les ménages ordinaires, en excluant les ménages collectifs. 
    Le principal matériau du toit est identifié pour chaque ménage et les proportions sont exprimées en pourcentage du total des ménages par milieu de résidence, province ou commune.
  </Typography>
</Paper>
                              </Grid>
                            </Grid>
                          </Container>
                        </ContentWrapper>
                      </MainContentWithDrawer>
                      <Footer />
                    </MainContainer>
                  );
                };
                
                export default RoofMaterialPage;